// src/pages/admin/Check.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  DataStateHandler,
  DataTable,
  ConfirmModal,
  Button,
  CheckboxField,
  Loading,
  showSuccess,
  showError,
  useScrollRestore,
  usePersistedState,
  SyncNetsuiteButton,
} from "@common";
import {
  checkColumns,
  canViewFinance,
  canEditFinance,
  buildCheckPayload,
  validateCheckData,
  calculateDdsDraw,
  formatMoney,
  torontoDateParts,
} from "@utils";
import { DEDUCTION_COLUMN_KEYS, isApprovalApproved } from "@utils/associateFee";
import { parseMonthDayFromDateString, isClearedBefore15th } from "@utils/checkDateUtils";
import {
  filterAssociateFeeEligibleRows,
  buildChecksByVendor,
  computeExpectedClearInfoByVendor,
  matchChecksToRows,
} from "@utils/checkMatching";
import { isOrganizerOrAdmin } from "@utils/roles";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import PractitionerQuickLookModal from "../../common/modal/PractitionerQuickLookModal";
import CheckPreview from "./CheckPreview";
import BulkCheckPreview from "./BulkCheckPreview";
import api from "../../api/api";
import { adminEndpoints } from "../../api/endpoints";
import {
  useMonthlyCompensations,
  useUpdatePractitionerByClinic,
  useUpdateMonthlyCompensationForClinic,
  useBulkApproveMonthlyCompensation,
  useCreateNetsuiteCheck,
  useBulkCreateNetsuiteCheck,
  useSearchNetsuiteChecks,
  useSearchNetsuiteCheckClearedDates,
} from "@api/hooks";
import { useAuth } from "@api/useAuth";
import ColumnToggle from "../../common/ui/ColumnToggle";

export default function Check({ months, clinics, month: legacyMonth, clinic: legacyClinic }) {
  const selectedMonths = useMemo(() => {
    if (Array.isArray(months) && months.length) return months;
    return legacyMonth ? [legacyMonth] : [];
  }, [months, legacyMonth]);
  const selectedClinics = useMemo(() => {
    if (Array.isArray(clinics) && clinics.length) return clinics;
    return legacyClinic ? [legacyClinic] : [];
  }, [clinics, legacyClinic]);
  const clinicFilter = useMemo(() => selectedClinics.join(","), [selectedClinics]);
  const month = selectedMonths[0] || "";
  const monthsParam = useMemo(() => selectedMonths.join(","), [selectedMonths]);
  const { user } = useAuth();
  const canView = canViewFinance(user); // Org, Admin, AM, Observer, Payroll, Bookkeeper, Finance Coord
  const canEdit = canEditFinance(user); // Org, Admin, Payroll, Finance Coord
  const isAdminOrOrg = isOrganizerOrAdmin(user);

  // Build a per-row memo of the form
  //   "Associate Fee - April 2026 - NL-G-EXP - Smiles Analytics"
  // Each clinic gets its own check, so the clinic code goes into the memo
  // and is also used when searching for already-created checks.
  const monthLabel = useMemo(() => {
    if (!month || typeof month !== "string") return "";
    const [y, m] = month.split("-").map((v) => parseInt(v, 10));
    if (!y || !m) return "";
    const name = new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long" });
    return `${name} ${y}`;
  }, [month]);

  const checkMemoFor = useCallback(
    (clinic) => {
      if (!monthLabel) return "";
      const code = (clinic || "").trim();
      return code
        ? `Associate Fee - ${monthLabel} - ${code} - Smiles Analytics`
        : `Associate Fee - ${monthLabel} - Smiles Analytics`;
    },
    [monthLabel],
  );

  // Memo substring used when searching for *existing* NetSuite Checks.
  // The backend combines this with `month` to OR-match common formats
  // ("March 2026", "Mar 2026", "03-2026", "3/2026", "2026-03", ...).
  const checkSearchMemo = "Associate Fee";

  const [modalOpen, setModalOpen] = useState(false);
  const [modalPractitioner, setModalPractitioner] = useState(null);
  const [modalOpenInEdit, setModalOpenInEdit] = useState(true);
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const saved = localStorage.getItem("check_hiddenColumns");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Deduction columns (purple) are hidden by default and grouped under a
  // single "Total Fee" column. The Total Fee header has a +/- toggle.
  const [deductionsExpanded, setDeductionsExpanded] = useState(() => {
    try {
      return localStorage.getItem("check_deductionsExpanded") === "true";
    } catch {
      return false;
    }
  });
  const toggleDeductions = useCallback(() => {
    setDeductionsExpanded((v) => {
      const next = !v;
      try {
        localStorage.setItem("check_deductionsExpanded", String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Approval state
  const [approveConfirmRow, setApproveConfirmRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);

  // Check preview state: { clinic, row, payload } | null
  const [checkPreview, setCheckPreview] = useState(null);

  // "Cleared by day" drill-in: when a row in the daily breakdown is clicked,
  // open a modal listing the practitioners whose checks cleared that day.
  const [clearedDayDetail, setClearedDayDetail] = useState(null);
  const [checksPanelOpen, setChecksPanelOpen] = useState(false);

  // Bulk check preview state.
  //   bulkPreview.items   = [{ key, clinic, row, payload }, ...]
  //   bulkPreview.results = null | { summary, results } once the batch has run
  const [bulkPreview, setBulkPreview] = useState(null);

  // Vendor+clinic keys we've just successfully created a check for in
  // this session. Used to flip the "Create" button to a "Done!!" label
  // without needing to refetch the (slow) NetSuite check search.
  const [justCreatedKeys, setJustCreatedKeys] = useState(() => new Set());

  const updateMut = useUpdatePractitionerByClinic();
  const approveMut = useUpdateMonthlyCompensationForClinic();
  const bulkApproveMut = useBulkApproveMonthlyCompensation();
  const createCheckMut = useCreateNetsuiteCheck();
  const bulkCreateCheckMut = useBulkCreateNetsuiteCheck();

  const { data, isLoading, error } = useMonthlyCompensations(
    {
      month: monthsParam,
      ...(clinicFilter ? { clinic: clinicFilter } : {}),
    },
    {
      // No clinic filter means the backend returns EVERY clinic's rows, so an
      // empty selection must not fire the request at all.
      enabled: selectedMonths.length > 0 && canView && !!clinicFilter,
      refetchInterval: false,
      refetchIntervalInBackground: false,
    },
  );

  // Gate on canView as well as `enabled`: a disabled useQuery still returns
  // whatever sits in the cache under the same key, and Cash Flow fetches the
  // same ["monthly-compensation", filters] key. Without this, a role without
  // view access sees rows only until the cache is dropped (e.g. on refresh).
  const rows = useMemo(
    () => (canView ? filterAssociateFeeEligibleRows(data?.results ?? data ?? []) : []),
    [data, canView],
  );

  // Preserve & restore window scroll across unmount/remount (tab switch).
  useScrollRestore(`check:scroll:${month || ""}:${clinicFilter || ""}`, !isLoading);

  // Look up existing NetSuite checks for all visible vendors + this month's memo.
  const vendorIds = useMemo(() => {
    const seen = new Set();
    for (const r of rows) {
      const vid = r.netsuite_vendor_id;
      if (vid !== null && vid !== undefined && vid !== "") {
        seen.add(String(vid));
      }
    }
    return Array.from(seen);
  }, [rows]);

  const existingChecksQuery = useSearchNetsuiteChecks(
    {
      entities: vendorIds,
      memo: checkSearchMemo,
      month,
    },
    { refetchInterval: false, refetchIntervalInBackground: false },
  );

  const clearedDatesQuery = useSearchNetsuiteCheckClearedDates(
    { netsuiteVendorIds: vendorIds },
    { refetchInterval: false, refetchIntervalInBackground: false },
  );

  // Expected clear date per vendor is based on historical cleared checks:
  // use the average of day-of-month from latest cleared dates (up to 5).
  const expectedClearInfoByVendor = useMemo(
    () => computeExpectedClearInfoByVendor(clearedDatesQuery.data, parseMonthDayFromDateString),
    [clearedDatesQuery.data],
  );

  const expectedClearLoading =
    vendorIds.length > 0 && (clearedDatesQuery.isLoading || clearedDatesQuery.isFetching);

  // List of NetSuite checks returned for this month, grouped by vendor id.
  // The backend no longer filters by grosstotal, so each vendor may have
  // multiple checks for the same memo+month — we surface all of them.
  const checksByVendor = useMemo(
    () =>
      buildChecksByVendor(
        existingChecksQuery.data?.items ??
          existingChecksQuery.data?.results ??
          existingChecksQuery.data ??
          [],
      ),
    [existingChecksQuery.data],
  );

  // Map each row to the checks that belong to it. See checkMatching.js for
  // the subset-sum matching rules (a vendor's checks are assigned to rows
  // by matching amounts to each row's expected DDS draw).
  const rowChecksMap = useMemo(
    () => matchChecksToRows(rows, checksByVendor, { getExpectedAmount: calculateDdsDraw }),
    [rows, checksByVendor],
  );

  // Per-row lookup: returns the checks that have been assigned to this
  // row (by single-check amount match, multi-check subset-sum match, or
  // as leftovers attached to the vendor's first row).
  const findExistingChecksForRow = useCallback(
    (r) => {
      if (!r) return [];
      return rowChecksMap.get(r.id) || [];
    },
    [rowChecksMap],
  );

  // Aggregate summary across every check returned for visible rows. Each
  // row may contribute multiple checks; we sum counts and amounts.
  const checksSummary = useMemo(() => {
    let total = 0;
    let cleared = 0;
    let totalAmount = 0;
    let clearedAmount = 0;
    const seenIds = new Set();
    for (const r of rows) {
      const list = findExistingChecksForRow(r);
      for (const c of list) {
        // De-dup by transactionnumber so the same check counted across
        // multiple rows (same vendor on different clinics) isn't double-summed.
        const key = c.transactionnumber
          ? `tn:${c.transactionnumber}`
          : `obj:${c.trandate}|${c.amount}`;
        if (seenIds.has(key)) continue;
        seenIds.add(key);
        total += 1;
        totalAmount += Number(c.amount) || 0;
        if (c.cleared) {
          cleared += 1;
          clearedAmount += Number(c.amount) || 0;
        }
      }
    }
    return { total, cleared, totalAmount, clearedAmount };
  }, [rows, findExistingChecksForRow]);

  // Per-day breakdown for the hover popover. Cleared checks are grouped
  // by `cleareddate`; uncleared checks are grouped under "Uncleared".
  // De-duplicates by transactionnumber to avoid double-counting a check
  // that appears on multiple rows.
  const checksDailyBreakdown = useMemo(() => {
    const byDay = new Map();
    const seenIds = new Set();
    for (const r of rows) {
      const list = findExistingChecksForRow(r);
      for (const c of list) {
        const key = c.transactionnumber
          ? `tn:${c.transactionnumber}`
          : `obj:${c.trandate}|${c.amount}`;
        if (seenIds.has(key)) continue;
        seenIds.add(key);
        const day = c.cleared ? c.cleareddate || c.trandate : "__uncleared__";
        const entry = byDay.get(day) || {
          day,
          count: 0,
          amount: 0,
          cleared: !!c.cleared,
          items: [],
        };
        entry.count += 1;
        entry.amount += Number(c.amount) || 0;
        entry.items.push({
          key,
          practitioner_name: r.practitioner_name,
          clinic: r.clinic,
          amount: Number(c.amount) || 0,
          transactionnumber: c.transactionnumber || null,
          trandate: c.trandate || null,
          cleareddate: c.cleareddate || null,
        });
        byDay.set(day, entry);
      }
    }
    const cleared = [];
    let uncleared = null;
    for (const entry of byDay.values()) {
      // Sort each day's practitioner items by clinic then name for stable display.
      entry.items.sort((a, b) => {
        const c = String(a.clinic || "").localeCompare(String(b.clinic || ""));
        if (c !== 0) return c;
        return String(a.practitioner_name || "").localeCompare(String(b.practitioner_name || ""));
      });
      if (entry.day === "__uncleared__") uncleared = entry;
      else cleared.push(entry);
    }
    cleared.sort((a, b) => {
      const ta = Date.parse(a.day);
      const tb = Date.parse(b.day);
      if (Number.isNaN(ta) && Number.isNaN(tb)) return String(a.day).localeCompare(String(b.day));
      if (Number.isNaN(ta)) return 1;
      if (Number.isNaN(tb)) return -1;
      return ta - tb;
    });
    return { cleared, uncleared };
  }, [rows, findExistingChecksForRow]);

  // Business rule: checks clearing before day 15 are unexpected and should
  // stand out visually in the cleared-by-day panel.
  const isUnexpectedClearedDay = useCallback(
    (day) => {
      if (!day || typeof day !== "string") return false;
      const parts = parseMonthDayFromDateString(day);
      if (!parts) return false;
      const { monthNum, dayNum } = parts;

      const parsed = new Date(day);
      const year = Number.isNaN(parsed.getTime()) ? null : parsed.getFullYear();
      if (!year) return dayNum < 15;

      const monthPrefix = `${year}-${String(monthNum).padStart(2, "0")}`;
      // If a single month is selected, only flag days within that month.
      if (month && /^\d{4}-\d{2}$/.test(month)) {
        if (monthPrefix !== month) return false;
      }
      return dayNum < 15;
    },
    [month],
  );

  const unexpectedClearedSummary = useMemo(() => {
    const flagged = checksDailyBreakdown.cleared.filter((d) => isUnexpectedClearedDay(d.day));
    return {
      checks: flagged.reduce((s, d) => s + (Number(d.count) || 0), 0),
      amount: flagged.reduce((s, d) => s + (Number(d.amount) || 0), 0),
    };
  }, [checksDailyBreakdown.cleared, isUnexpectedClearedDay]);

  // Sort: clinic ASC, DDS before HYG, then practitioner name
  const sortedRows = useMemo(() => {
    const typeOrder = { DDS: 0, HYG: 1 };
    return [...rows].sort((a, b) => {
      const ca = (a.clinic || "").localeCompare(b.clinic || "");
      if (ca !== 0) return ca;
      const ta = typeOrder[(a.practitioner_type || "").toUpperCase()] ?? 99;
      const tb = typeOrder[(b.practitioner_type || "").toUpperCase()] ?? 99;
      if (ta !== tb) return ta - tb;
      return (a.practitioner_name || "").localeCompare(b.practitioner_name || "");
    });
  }, [rows]);

  // Filter rows by check status: "all" | "available" | "missing" | "created"
  // - created:   a NetSuite check already exists for this vendor + memo
  // - available: no existing check AND record is approved + passes validation (ready to create)
  // - missing:   no existing check AND NOT available (needs approval or has a validation blocker)
  const [statusFilter, setStatusFilter] = usePersistedState("check:statusFilter", "all");
  // Secondary filter that only applies to rows with an existing NetSuite check.
  // "all" | "cleared" | "uncleared" | "pre15"
  const [clearedFilter, setClearedFilter] = usePersistedState("check:clearedFilter", "all");

  const hasExistingCheck = useCallback(
    (r) => {
      const vid = r.netsuite_vendor_id;
      if (vid === null || vid === undefined || vid === "") return false;
      const code = String(r.clinic || "").toUpperCase();
      const key = `${String(vid)}::${code}`;
      if (justCreatedKeys.has(key)) return true;
      return findExistingChecksForRow(r).length > 0;
    },
    [findExistingChecksForRow, justCreatedKeys],
  );

  const isEligibleToCreate = useCallback(
    (r) => !!isApprovalApproved(r.admin_approval) && validateCheckData({ row: r }).ok,
    [],
  );

  const statusCounts = useMemo(() => {
    let created = 0;
    let missing = 0;
    let available = 0;
    let cleared = 0;
    let uncleared = 0;
    let pre15 = 0;
    for (const r of sortedRows) {
      if (hasExistingCheck(r)) {
        created += 1;
        const list = findExistingChecksForRow(r);
        if (list.some((c) => c.cleared)) cleared += 1;
        if (list.some((c) => !c.cleared)) uncleared += 1;
        if (list.some((c) => c.cleared && isClearedBefore15th(c.cleareddate || c.trandate))) {
          pre15 += 1;
        }
      } else if (isEligibleToCreate(r)) {
        available += 1;
      } else {
        missing += 1;
      }
    }
    return { all: sortedRows.length, available, missing, created, cleared, uncleared, pre15 };
  }, [sortedRows, hasExistingCheck, isEligibleToCreate, findExistingChecksForRow]);

  const filteredRows = useMemo(() => {
    const matchesCleared = (r) => {
      if (clearedFilter === "all") return true;
      const list = findExistingChecksForRow(r);
      if (list.length === 0) return false;
      if (clearedFilter === "cleared") return list.some((c) => c.cleared);
      if (clearedFilter === "uncleared") return list.some((c) => !c.cleared);
      if (clearedFilter === "pre15") {
        return list.some((c) => c.cleared && isClearedBefore15th(c.cleareddate || c.trandate));
      }
      return true;
    };
    const base =
      statusFilter === "all"
        ? sortedRows
        : sortedRows.filter((r) => {
            const exists = hasExistingCheck(r);
            if (statusFilter === "created") return exists;
            if (exists) return false;
            const eligible = isEligibleToCreate(r);
            if (statusFilter === "available") return eligible;
            if (statusFilter === "missing") return !eligible;
            return true;
          });
    if (clearedFilter === "all") return base;
    return base.filter(matchesCleared);
  }, [
    sortedRows,
    statusFilter,
    clearedFilter,
    hasExistingCheck,
    isEligibleToCreate,
    findExistingChecksForRow,
  ]);

  // Group by clinic (preserves sort order)
  const clinicGroups = useMemo(() => {
    const map = new Map();
    for (const r of filteredRows) {
      const k = r.clinic || "—";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    return Array.from(map.entries());
  }, [filteredRows]);

  // Open modal — fetch full practitioner profile
  const openPractitionerModal = useCallback(async (row, { inEdit }) => {
    try {
      const res = await api.get(
        adminEndpoints.GET_PRACTITIONER_BY_CLINIC(row.clinic, row.practitioner_id),
      );
      setModalPractitioner(res.data);
      setModalOpenInEdit(inEdit);
      setModalOpen(true);
    } catch (err) {
      showError(getApiErrorMessage(err));
    }
  }, []);

  const handleUpdateClick = useCallback(
    (row) => openPractitionerModal(row, { inEdit: true }),
    [openPractitionerModal],
  );
  const handlePractitionerNameClick = useCallback(
    (row) => openPractitionerModal(row, { inEdit: false }),
    [openPractitionerModal],
  );

  async function handleSavePractitioner(original, payload) {
    await updateMut.mutateAsync({
      clinicCode: original.clinic,
      id: original.id,
      data: payload,
    });
    setModalOpen(false);
    setModalPractitioner(null);
  }

  // ── Single approval ──
  const handleApproveClick = useCallback((row) => {
    setApproveConfirmRow(row);
  }, []);

  const confirmApprove = useCallback(async () => {
    if (!approveConfirmRow) return;
    try {
      await approveMut.mutateAsync({
        clinicCode: approveConfirmRow.clinic,
        id: approveConfirmRow.id,
        data: {
          admin_approval: {
            approved: true,
            user: user?.email || "",
          },
        },
      });
      showSuccess(`Approved ${approveConfirmRow.practitioner_name}`);
    } catch (err) {
      showError(getApiErrorMessage(err));
    } finally {
      setApproveConfirmRow(null);
    }
  }, [approveConfirmRow, approveMut, user?.email]);

  // ── Bulk approval ──
  const unapprovedRows = useMemo(
    () => rows.filter((r) => !isApprovalApproved(r.admin_approval)),
    [rows],
  );

  const toggleSelectRow = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allUnapprovedIds = unapprovedRows.map((r) => r.id);
      const allSelected =
        allUnapprovedIds.length > 0 && allUnapprovedIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(allUnapprovedIds);
    });
  }, [unapprovedRows]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [month, clinicFilter]);

  const selectedUnapproved = useMemo(
    () => rows.filter((r) => !isApprovalApproved(r.admin_approval) && selectedIds.has(r.id)),
    [rows, selectedIds],
  );

  const handleBulkApproveClick = useCallback(() => {
    if (selectedUnapproved.length === 0) {
      showError("No unapproved rows selected.");
      return;
    }
    setBulkApproveOpen(true);
  }, [selectedUnapproved]);

  const handleApproveAllClick = useCallback(() => {
    if (unapprovedRows.length === 0) {
      showError("All records are already approved.");
      return;
    }
    setSelectedIds(new Set(unapprovedRows.map((r) => r.id)));
    setBulkApproveOpen(true);
  }, [unapprovedRows]);

  const confirmBulkApprove = useCallback(async () => {
    setBulkApproving(true);
    try {
      const ids = selectedUnapproved.map((r) => r.id);
      await bulkApproveMut.mutateAsync({ ids, approved: true });
      showSuccess(`All ${ids.length} records approved!`);
    } catch (err) {
      showError(getApiErrorMessage(err));
    } finally {
      setBulkApproving(false);
      setBulkApproveOpen(false);
      setSelectedIds(new Set());
    }
  }, [selectedUnapproved, bulkApproveMut]);

  // Default check date to the 15th of the *current* calendar month (the
  // typical mid-month payroll run date), regardless of which payroll month
  // is being processed.
  const defaultCheckDate = useMemo(() => {
    const now = torontoDateParts();
    const y = now.year;
    const m = String(now.month).padStart(2, "0");
    return `${y}-${m}-15`;
  }, []);

  // ── Create Check (per row) ──
  // Each clinic gets its own check, with the clinic code embedded in
  // the memo (e.g. "Associate Fee - April 2026 - NL-G-EXP - Smiles
  // Analytics"). Build + validate the payload, then open the preview.
  const handleCreateCheck = useCallback(
    (row) => {
      try {
        const payload = buildCheckPayload({
          trandate: defaultCheckDate,
          memo: checkMemoFor(row.clinic),
          row,
        });
        setCheckPreview({ clinic: row.clinic, row, payload });
      } catch (err) {
        showError(getApiErrorMessage(err));
      }
    },
    [checkMemoFor, defaultCheckDate],
  );

  // Actually send the payload to NetSuite after user confirms.
  const confirmCreateCheck = useCallback(async () => {
    if (!checkPreview) return;
    try {
      await createCheckMut.mutateAsync(checkPreview.payload);
      const vid = checkPreview.row?.netsuite_vendor_id;
      const code = String(checkPreview.row?.clinic || "").toUpperCase();
      if (vid !== null && vid !== undefined && vid !== "" && code) {
        setJustCreatedKeys((prev) => {
          const next = new Set(prev);
          next.add(`${String(vid)}::${code}`);
          return next;
        });
      }
      showSuccess(
        `Check created for ${checkPreview.row.practitioner_name} (${checkPreview.clinic}).`,
      );
      setCheckPreview(null);
    } catch (err) {
      showError(getApiErrorMessage(err));
    }
  }, [checkPreview, createCheckMut]);

  // ── Bulk Create Checks ──
  // Gathers every "available" row (approved + valid + no existing check),
  // builds a payload for each, and opens the bulk preview modal.
  const handleBulkCreateCheckClick = useCallback(() => {
    const items = [];
    for (const r of sortedRows) {
      if (hasExistingCheck(r)) continue;
      if (!isEligibleToCreate(r)) continue;
      try {
        const payload = buildCheckPayload({
          trandate: defaultCheckDate,
          memo: checkMemoFor(r.clinic),
          row: r,
        });
        const vid = r.netsuite_vendor_id;
        const code = String(r.clinic || "").toUpperCase();
        items.push({
          key: `${String(vid)}::${code}`,
          clinic: r.clinic,
          row: r,
          payload,
        });
      } catch {
        // Skip invalid rows silently — they are already shown as
        // "missing" in the status filter.
      }
    }
    if (items.length === 0) {
      showError("No checks available to create.");
      return;
    }
    setBulkPreview({ items, results: null });
  }, [sortedRows, hasExistingCheck, isEligibleToCreate, defaultCheckDate, checkMemoFor]);

  // Send the bulk batch. Each step is processed independently on the
  // backend; failures in one step do not abort the others.
  const confirmBulkCreateCheck = useCallback(async () => {
    if (!bulkPreview?.items?.length) return;
    // If we've already run the batch, the confirm button just closes the modal.
    if (bulkPreview.results) {
      setBulkPreview(null);
      return;
    }
    try {
      const steps = bulkPreview.items.map((it) => ({ body: it.payload }));
      const response = await bulkCreateCheckMut.mutateAsync({ steps });
      const results = response?.results ?? [];
      // Mark successful (vendor, clinic) pairs as "just created" so the
      // per-row UI flips immediately without waiting for a refetch.
      setJustCreatedKeys((prev) => {
        const next = new Set(prev);
        for (const r of results) {
          if (!r?.ok) continue;
          const item = bulkPreview.items[r.index];
          if (!item) continue;
          const vid = item.row?.netsuite_vendor_id;
          const code = String(item.row?.clinic || "").toUpperCase();
          if (vid !== null && vid !== undefined && vid !== "" && code) {
            next.add(`${String(vid)}::${code}`);
          }
        }
        return next;
      });
      const summary = response?.summary ?? {
        total: bulkPreview.items.length,
        succeeded: results.filter((r) => r?.ok).length,
        failed: results.filter((r) => !r?.ok).length,
      };
      if (summary.failed === 0) {
        showSuccess(`Created ${summary.succeeded} check${summary.succeeded === 1 ? "" : "s"}.`);
      } else if (summary.succeeded === 0) {
        showError(`All ${summary.failed} check${summary.failed === 1 ? "" : "s"} failed.`);
      } else {
        showError(`${summary.succeeded} succeeded, ${summary.failed} failed. See details below.`);
      }
      setBulkPreview((prev) => (prev ? { ...prev, results: { summary, results } } : prev));
    } catch (err) {
      showError(getApiErrorMessage(err));
    }
  }, [bulkPreview, bulkCreateCheckMut]);

  // ── Export filtered rows to CSV ──
  const handleExportCsv = useCallback(() => {
    if (filteredRows.length === 0) {
      showError("No rows to export.");
      return;
    }
    const csvEscape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = [
      "Clinic",
      "Practitioner",
      "Type",
      "DDS Draw",
      "Production",
      "Collection",
      "Paid Based On",
      "Associate Fee %",
      "Lab Cost",
      "Material Cost",
      "Invisalign Cost",
      "Anesthesia Fee",
      "Anesthesiologist Fee",
      "Facility Fee",
      "Botox",
      "Production Adjustment",
      "Other Profit",
      "Other Deduction",
      "Approved",
      "NetSuite Vendor ID",
    ];
    const lines = [headers.map(csvEscape).join(",")];
    for (const r of filteredRows) {
      const otherProfit = Array.isArray(r.other_profit)
        ? r.other_profit.reduce((s, t) => s + (Number(t?.profit) || 0), 0)
        : 0;
      const otherDeduction = Array.isArray(r.other_deduction)
        ? r.other_deduction.reduce((s, t) => {
            const explicit = Number(t?.amount);
            if (Number.isFinite(explicit) && explicit !== 0) return s + explicit;
            if (t?.hour_rate !== undefined || t?.hours !== undefined) {
              return s + (Number(t?.hour_rate) || 0) * (Number(t?.hours) || 0);
            }
            return s + (Number(t?.profit) || 0);
          }, 0)
        : 0;
      lines.push(
        [
          r.clinic,
          r.practitioner_name,
          r.practitioner_type,
          calculateDdsDraw(r),
          r.production?.total, // ?.total — serializer returns nested { total, breakdown }
          r.collection?.total,
          r.paid_based_on,
          r.associate_fee_percentage,
          r.lab_cost?.total,
          r.material_cost?.total,
          r.invisalign_cost?.total,
          r.anesthesia_fee?.total,
          r.anesthesiologist_fee?.total,
          r.facility_fee?.total,
          r.botox?.total,
          r.production_adjustment?.total,
          otherProfit,
          otherDeduction,
          isApprovalApproved(r.admin_approval) ? "Yes" : "No",
          r.netsuite_vendor_id,
        ]
          .map(csvEscape)
          .join(","),
      );
    }
    const blob = new Blob(["\uFEFF" + lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `check-${statusFilter}-${month || "all"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    showSuccess(`Exported ${filteredRows.length} row${filteredRows.length !== 1 ? "s" : ""}.`);
  }, [filteredRows, statusFilter, month]);

  const allColumns = useMemo(
    () =>
      checkColumns(canEdit ? handleUpdateClick : null, {
        onApprove: canEdit ? handleApproveClick : null,
        onCreateCheck: canEdit ? handleCreateCheck : null,
        onPractitionerClick: handlePractitionerNameClick,
        findExistingChecksForRow,
        justCreatedKeys,
        expectedClearInfoByVendor,
        expectedClearLoading,
        deductionsExpanded,
        onToggleDeductions: toggleDeductions,
      }),
    [
      canEdit,
      handleUpdateClick,
      handleApproveClick,
      handleCreateCheck,
      handlePractitionerNameClick,
      findExistingChecksForRow,
      justCreatedKeys,
      expectedClearInfoByVendor,
      expectedClearLoading,
      deductionsExpanded,
      toggleDeductions,
    ],
  );

  const columns = useMemo(
    () =>
      allColumns.filter((c) => {
        if (hiddenColumns.has(c.key)) return false;
        if (!deductionsExpanded && DEDUCTION_COLUMN_KEYS.includes(c.key)) return false;
        return true;
      }),
    [allColumns, hiddenColumns, deductionsExpanded],
  );

  const toggleColumn = useCallback((key) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      localStorage.setItem("check_hiddenColumns", JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Prepend selection checkbox column (admins only)
  const columnsWithSelect = useMemo(() => {
    if (!isAdminOrOrg || unapprovedRows.length === 0) return columns;
    const allUnapprovedIds = unapprovedRows.map((r) => r.id);
    const allSelected =
      allUnapprovedIds.length > 0 && allUnapprovedIds.every((id) => selectedIds.has(id));
    return [
      {
        key: "_select",
        header: <CheckboxField checked={allSelected} onChange={toggleSelectAll} />,
        width: 40,
        render: (r) =>
          isApprovalApproved(r.admin_approval) ? null : (
            <CheckboxField checked={selectedIds.has(r.id)} onChange={() => toggleSelectRow(r.id)} />
          ),
      },
      ...columns,
    ];
  }, [columns, unapprovedRows, selectedIds, toggleSelectAll, toggleSelectRow, isAdminOrOrg]);

  return (
    <div className="w-full overflow-x-hidden">
      {clearedDayDetail && (
        <ConfirmModal
          isOpen={!!clearedDayDetail}
          onClose={() => setClearedDayDetail(null)}
          onConfirm={() => setClearedDayDetail(null)}
          title={`Cleared on ${clearedDayDetail.day}`}
          size="lg"
          confirmText="Close"
          confirmVariant="primary"
          cancelText="Cancel"
          message={
            <div>
              <div className="mb-3 flex items-center justify-between text-sm text-palette-gray-heading">
                <span>
                  <span className="font-semibold">{clearedDayDetail.count}</span> check
                  {clearedDayDetail.count === 1 ? "" : "s"} cleared
                </span>
                <span className="font-semibold text-palette-emerald-text">
                  {formatMoney(clearedDayDetail.amount)}
                </span>
              </div>
              <div className="max-h-[24rem] overflow-y-auto rounded border border-palette-gray-divider">
                <table className="w-full text-sm">
                  <thead className="bg-palette-gray-soft sticky top-0">
                    <tr className="text-left text-palette-gray-badge border-b border-palette-gray-divider">
                      <th className="px-3 py-2 font-medium">Practitioner</th>
                      <th className="px-3 py-2 font-medium">Clinic</th>
                      <th className="px-3 py-2 font-medium">Check #</th>
                      <th className="px-3 py-2 font-medium text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clearedDayDetail.items.map((it) => (
                      <tr
                        key={it.key}
                        className="border-b border-palette-gray-subtle last:border-0"
                      >
                        <td className="px-3 py-2 text-palette-gray-primary">
                          {it.practitioner_name}
                        </td>
                        <td className="px-3 py-2 text-palette-gray-heading">{it.clinic}</td>
                        <td className="px-3 py-2 text-palette-gray-heading tabular-nums">
                          {it.transactionnumber || "—"}
                        </td>
                        <td className="px-3 py-2 text-right text-palette-emerald-primary font-medium tabular-nums">
                          {formatMoney(it.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          }
        />
      )}
      {approveConfirmRow && (
        <ConfirmModal
          isOpen={!!approveConfirmRow}
          onClose={() => setApproveConfirmRow(null)}
          onConfirm={confirmApprove}
          title="Approve Record"
          message={`Approve associate fee record for ${approveConfirmRow.practitioner_name} (${approveConfirmRow.clinic})?`}
          confirmText="Approve"
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={approveMut.isPending}
        />
      )}
      {bulkApproveOpen && (
        <ConfirmModal
          isOpen={bulkApproveOpen}
          onClose={() => !bulkApproving && setBulkApproveOpen(false)}
          onConfirm={confirmBulkApprove}
          title="Approve Selected Records"
          message={
            <div>
              <p className="text-sm mb-2">
                Approve <strong>{selectedUnapproved.length}</strong> record
                {selectedUnapproved.length !== 1 ? "s" : ""}?
              </p>
              <div className="max-h-48 overflow-y-auto rounded border border-palette-gray-divider bg-palette-gray-soft p-2">
                {selectedUnapproved.map((r) => (
                  <div key={r.id} className="flex items-center justify-between py-1 text-sm">
                    <span className="font-medium">{r.practitioner_name}</span>
                    <span className="text-palette-gray-secondary">{r.clinic}</span>
                  </div>
                ))}
              </div>
            </div>
          }
          confirmText={bulkApproving ? `Approving...` : `Approve ${selectedUnapproved.length}`}
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={bulkApproving}
        />
      )}
      {modalOpen && (
        <PractitionerQuickLookModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setModalPractitioner(null);
          }}
          practitioner={modalPractitioner}
          canEdit={canEdit}
          openInEdit={modalOpenInEdit && canEdit}
          onSave={canEdit ? handleSavePractitioner : null}
        />
      )}
      {checkPreview && (
        <ConfirmModal
          isOpen={!!checkPreview}
          onClose={() => !createCheckMut.isPending && setCheckPreview(null)}
          onConfirm={confirmCreateCheck}
          title={`Create NetSuite Check — ${checkPreview.row?.practitioner_name ?? ""}`}
          size="xl"
          confirmText={createCheckMut.isPending ? "Sending..." : "Confirm & Send"}
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={createCheckMut.isPending}
          message={
            <CheckPreview
              preview={checkPreview}
              onChange={(patch) =>
                setCheckPreview((prev) =>
                  prev ? { ...prev, payload: { ...prev.payload, ...patch } } : prev,
                )
              }
            />
          }
        />
      )}
      {bulkPreview && (
        <ConfirmModal
          isOpen={!!bulkPreview}
          onClose={() => !bulkCreateCheckMut.isPending && setBulkPreview(null)}
          onConfirm={confirmBulkCreateCheck}
          title={
            bulkPreview.results
              ? `Bulk Check Results (${bulkPreview.results.summary?.succeeded ?? 0}/${
                  bulkPreview.results.summary?.total ?? bulkPreview.items.length
                } created)`
              : `Create ${bulkPreview.items.length} NetSuite Check${
                  bulkPreview.items.length === 1 ? "" : "s"
                }`
          }
          size="xl"
          confirmText={
            bulkCreateCheckMut.isPending
              ? "Sending..."
              : bulkPreview.results
                ? "Close"
                : `Confirm & Send (${bulkPreview.items.length})`
          }
          confirmVariant="primary"
          cancelText={bulkPreview.results ? "Close" : "Cancel"}
          isLoading={bulkCreateCheckMut.isPending}
          message={
            <BulkCheckPreview
              items={bulkPreview.items}
              results={bulkPreview.results}
              onChange={(idx, patch) =>
                setBulkPreview((prev) => {
                  if (!prev) return prev;
                  const nextItems = prev.items.map((it, i) =>
                    i === idx ? { ...it, payload: { ...it.payload, ...patch } } : it,
                  );
                  return { ...prev, items: nextItems };
                })
              }
            />
          }
        />
      )}
      <Card>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold">Check</h3>
            <ColumnToggle
              allColumns={allColumns}
              hiddenColumns={hiddenColumns}
              onChange={toggleColumn}
            />
            {isAdminOrOrg && <SyncNetsuiteButton source="check tab" />}
            <div
              className="inline-flex rounded-md border border-palette-gray-border overflow-hidden"
              role="group"
              aria-label="Filter practitioners by check status"
            >
              {[
                { key: "all", label: `All (${statusCounts.all})` },
                { key: "available", label: `Available (${statusCounts.available})` },
                { key: "missing", label: `Missing (${statusCounts.missing})` },
                { key: "created", label: `Created (${statusCounts.created})` },
              ].map((opt) => {
                const active = statusFilter === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setStatusFilter(opt.key)}
                    className={`px-3 py-1 text-xs font-medium transition-colors border-r last:border-r-0 border-palette-gray-border ${
                      active
                        ? "bg-palette-indigo-badge text-white"
                        : "bg-white text-palette-gray-heading hover:bg-palette-gray-soft"
                    }`}
                    aria-pressed={active}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div
              className="inline-flex rounded-md border border-palette-gray-border overflow-hidden"
              role="group"
              aria-label="Filter by NetSuite cleared status"
            >
              {[
                { key: "all", label: "All", activeCls: "bg-palette-indigo-badge text-white" },
                {
                  key: "cleared",
                  label: `Cleared (${statusCounts.cleared})`,
                  activeCls: "bg-palette-emerald-badge text-white",
                },
                {
                  key: "uncleared",
                  label: `Uncleared (${statusCounts.uncleared})`,
                  activeCls: "bg-palette-amber-label text-white",
                },
                {
                  key: "pre15",
                  label: `Cleared <15 (${statusCounts.pre15})`,
                  activeCls: "bg-palette-rose-badge text-white",
                },
              ].map((opt) => {
                const active = clearedFilter === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setClearedFilter(opt.key)}
                    className={`px-3 py-1 text-xs font-medium transition-colors border-r last:border-r-0 border-palette-gray-border ${
                      active
                        ? opt.activeCls
                        : "bg-white text-palette-gray-heading hover:bg-palette-gray-soft"
                    }`}
                    aria-pressed={active}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCsv}
                disabled={isLoading || filteredRows.length === 0}
              >
                Export CSV ({filteredRows.length})
              </Button>
              {selectedUnapproved.length > 0 && isAdminOrOrg && (
                <Button variant="outline" size="sm" onClick={handleBulkApproveClick}>
                  Approve Selected ({selectedUnapproved.length})
                </Button>
              )}
              {isAdminOrOrg && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleApproveAllClick}
                  disabled={isLoading || unapprovedRows.length === 0}
                >
                  Approve All
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleBulkCreateCheckClick}
                  disabled={
                    isLoading || existingChecksQuery.isFetching || statusCounts.available === 0
                  }
                  title={
                    existingChecksQuery.isFetching
                      ? "Checking NetSuite for existing checks…"
                      : statusCounts.available === 0
                        ? "No approved + valid rows available to create"
                        : "Create checks for all available rows"
                  }
                >
                  {existingChecksQuery.isFetching
                    ? "Checking NetSuite…"
                    : `Create Bulk Checks (${statusCounts.available})`}
                </Button>
              )}
            </div>
            {checksSummary.total > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setChecksPanelOpen((v) => !v)}
                  aria-expanded={checksPanelOpen}
                  className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 rounded-md border border-palette-emerald-border bg-palette-emerald-soft px-3 py-1.5 text-xs cursor-pointer hover:bg-palette-emerald-muted focus:outline-none focus:ring-2 focus:ring-palette-emerald-indicator"
                  title={
                    checksPanelOpen ? "Hide cleared-by-day details" : "Show cleared-by-day details"
                  }
                >
                  <span className="font-semibold text-palette-emerald-primary">
                    NetSuite Checks:
                  </span>
                  <span className="text-palette-emerald-text">
                    <span className="font-semibold">{checksSummary.cleared}</span>
                    <span className="text-palette-emerald-hover">
                      {" / "}
                      {checksSummary.total}
                    </span>{" "}
                    cleared
                  </span>
                  <span className="text-palette-amber-text">
                    <span className="font-semibold">
                      {checksSummary.total - checksSummary.cleared}
                    </span>{" "}
                    remaining
                  </span>
                  <span className="text-palette-emerald-text">
                    <span className="font-semibold">
                      {formatMoney(checksSummary.clearedAmount)}
                    </span>
                    <span className="text-palette-emerald-hover">
                      {" / "}
                      {formatMoney(checksSummary.totalAmount)}
                    </span>
                  </span>
                  <span className="text-palette-amber-text">
                    <span className="font-semibold">
                      {formatMoney(checksSummary.totalAmount - checksSummary.clearedAmount)}
                    </span>{" "}
                    remaining
                  </span>
                </button>
                {checksPanelOpen && (
                  <div className="absolute right-0 top-full mt-2 z-30 w-[32rem] max-h-[32rem] overflow-y-auto rounded-lg border border-palette-emerald-border bg-white shadow-xl p-5 text-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-base font-semibold text-palette-emerald-primary">
                        Cleared by day
                      </div>
                      <button
                        type="button"
                        onClick={() => setChecksPanelOpen(false)}
                        className="text-palette-gray-secondary hover:text-palette-gray-text text-lg leading-none focus:outline-none"
                        title="Close"
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                    {checksDailyBreakdown.cleared.length === 0 ? (
                      <div className="text-palette-gray-secondary italic text-sm">
                        No cleared checks yet.
                      </div>
                    ) : (
                      <>
                        {unexpectedClearedSummary.checks > 0 && (
                          <div className="mb-2 rounded-md border border-palette-rose-border bg-palette-rose-soft px-3 py-2 text-xs text-palette-rose-heading">
                            {unexpectedClearedSummary.checks} check
                            {unexpectedClearedSummary.checks === 1 ? "" : "s"} cleared before day 15
                            ({formatMoney(unexpectedClearedSummary.amount)}).
                          </div>
                        )}
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-palette-gray-badge border-b border-palette-gray-divider">
                              <th className="py-2 font-medium">Date</th>
                              <th className="py-2 font-medium text-right">Checks</th>
                              <th className="py-2 font-medium text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {checksDailyBreakdown.cleared.map((d) => {
                              const isUnexpected = isUnexpectedClearedDay(d.day);
                              return (
                                <tr
                                  key={d.day}
                                  className={`border-b border-palette-gray-subtle last:border-0 ${isUnexpected ? "bg-palette-rose-soft/60" : ""}`}
                                >
                                  <td
                                    className={`py-2 ${isUnexpected ? "text-palette-rose-heading font-medium" : "text-palette-emerald-text"}`}
                                  >
                                    {d.day}
                                    {isUnexpected && (
                                      <span className="ml-2 rounded bg-palette-rose-muted px-1.5 py-0.5 text-[10px] font-semibold text-palette-rose-heading">
                                        before 15
                                      </span>
                                    )}
                                  </td>
                                  <td
                                    className={`py-2 text-right font-medium ${isUnexpected ? "text-palette-rose-heading" : "text-palette-emerald-primary"}`}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => setClearedDayDetail(d)}
                                      className={`underline decoration-dotted underline-offset-2 rounded focus:outline-none focus:ring-2 ${
                                        isUnexpected
                                          ? "hover:text-palette-rose-badge focus:ring-palette-rose-ring"
                                          : "hover:text-palette-emerald-hover focus:ring-palette-emerald-indicator"
                                      }`}
                                      title="Show practitioners cleared on this day"
                                    >
                                      {d.count}
                                    </button>
                                  </td>
                                  <td
                                    className={`py-2 text-right font-medium ${isUnexpected ? "text-palette-rose-heading" : "text-palette-emerald-primary"}`}
                                  >
                                    {formatMoney(d.amount)}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="font-semibold text-base">
                              <td className="py-2 text-palette-emerald-primary">Total cleared</td>
                              <td className="py-2 text-right text-palette-emerald-primary">
                                {checksSummary.cleared}
                              </td>
                              <td className="py-2 text-right text-palette-emerald-primary">
                                {formatMoney(checksSummary.clearedAmount)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </>
                    )}
                    {checksDailyBreakdown.uncleared && (
                      <div className="mt-4 pt-3 border-t border-palette-gray-divider flex items-center justify-between text-palette-amber-text text-base">
                        <span className="font-semibold">Uncleared</span>
                        <span>
                          <span className="font-semibold">
                            {checksDailyBreakdown.uncleared.count}
                          </span>{" "}
                          ·{" "}
                          <span className="font-semibold">
                            {formatMoney(checksDailyBreakdown.uncleared.amount)}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <Card bodyClassName="p-0">
          <DataStateHandler
            isLoading={isLoading}
            error={error}
            isEmpty={!isLoading && rows.length === 0}
            loadingMessage="Loading checks..."
            emptyMessage={
              clinicFilter ? "No data for this month" : "Select one or more clinics to view checks."
            }
            showLoadingSpinner={true}
          >
            <div className="relative max-w-full overflow-x-auto">
              {existingChecksQuery.isFetching && (
                <div className="absolute inset-0 z-10 flex items-start justify-center pt-6 bg-white/70 backdrop-blur-sm pointer-events-none">
                  <Loading text="Checking NetSuite for existing checks..." size="lg" />
                </div>
              )}
              <div className="min-w-max space-y-8 p-4">
                {clinicGroups.length === 0 ? (
                  <div className="text-sm text-palette-gray-secondary italic px-2 py-6 text-center">
                    {statusFilter === "all"
                      ? "No clinics to display."
                      : "No practitioners match the selected filter."}
                  </div>
                ) : (
                  clinicGroups.map(([clinic, clinicRows]) => {
                    const allApproved = clinicRows.every((r) =>
                      isApprovalApproved(r.admin_approval),
                    );
                    const pendingCount = clinicRows.filter(
                      (r) => !isApprovalApproved(r.admin_approval),
                    ).length;
                    return (
                      <div
                        key={clinic}
                        className="rounded-lg border border-palette-gray-divider bg-white"
                      >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-palette-gray-divider bg-palette-gray-soft rounded-t-lg">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-base font-semibold text-palette-gray-text">
                              {clinic}
                            </span>
                            <span className="text-xs text-palette-gray-secondary">
                              ({clinicRows.length} practitioner
                              {clinicRows.length !== 1 ? "s" : ""})
                            </span>
                            {!allApproved && (
                              <span className="text-xs text-palette-amber-heading bg-palette-amber-muted rounded px-2 py-0.5">
                                {pendingCount} pending approval
                              </span>
                            )}
                          </div>
                        </div>
                        <DataTable columns={columnsWithSelect} data={clinicRows} rowKey="id" />
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </DataStateHandler>
        </Card>
      </Card>
    </div>
  );
}
